import { uniq } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import type CheckableMap from '@/models/CheckableMap'

import type { _MakeProblem } from './MakeProblem'

export class MakeProblemColl {
  constructor(private problemMap: CheckableMap<_MakeProblem['id'], _MakeProblem>) {
    makeAutoObservable(this)
  }

  get isEmpty() {
    return this.problemMap.toArr.length === 0
  }

  get toArr() {
    return [...this.problemMap.values()]
  }

  get entries() {
    return this.problemMap.entries()
  }

  get originalProblemList() {
    return this.toArr
      .map((problem, index) => ({
        id: problem.problemSource?.id,
        problemImageUrl: problem.problemSource?.problemImageUrl,
        relationProblemIndex: index,
        conceptName: problem.conceptName,
      }))
      .filter((v) => !!v.id)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
  }

  get problemIdList() {
    return uniq(this.toArr.map(({ id }) => id))
  }

  // 가상 음수 키(미복사 나의DB 문항)는 BE 에 존재하지 않는 problem id 라 전송 payload 에서 제외한다
  get persistedProblemIdList() {
    return this.problemIdList.filter((id) => id > 0)
  }

  // 미복사 나의DB 문항은 problemIdList 로 제외할 수 없어 detailId 목록으로 BE 중복 등장을 막는다
  get uncopiedMyDbProblemDetailIdList() {
    return this.toArr
      .filter(
        (problem): problem is _MakeProblem & { myDbProblemDetailId: number } =>
          problem.id < 0 && problem.myDbProblemDetailId != null,
      )
      .map(({ myDbProblemDetailId }) => myDbProblemDetailId)
  }

  get conceptIdList() {
    return uniq(this.toArr.map(({ conceptId }) => conceptId))
  }

  get curriculumIdList() {
    return uniq(this.toArr.map(({ curriculumId }) => curriculumId))
  }

  get baseWorkbookProblemIdList(): number[] {
    return uniq(
      this.toArr
        .map(({ baseWorkbookProblemId }) => baseWorkbookProblemId)
        .filter((id) => id !== null),
    ) as number[]
  }

  get isFullyChecked() {
    return this.problemMap.isFullyChecked
  }

  summary() {
    return this.toArr.map(({ id, type }) => ({
      id,
      type,
    }))
  }

  values() {
    return this.problemMap.values()
  }

  set(k, v) {
    return this.problemMap.set(k, v)
  }

  delete(id) {
    return this.problemMap.delete(id)
  }

  deletes(keys = [...this.problemMap.keys()]) {
    return this.problemMap.deletes(keys)
  }

  checkSize() {
    return this.problemMap.checkSize
  }

  toCheckedValues() {
    return this.problemMap.toCheckedValues
  }

  toCheckedSize() {
    return this.problemMap.toCheckedValues.length
  }

  get(id: _MakeProblem['id']) {
    return this.problemMap.get(id)
  }

  has(id: _MakeProblem['id']) {
    return this.problemMap.has(id)
  }

  toggleAllCheck() {
    return this.problemMap.toggleAllCheck()
  }

  clearAllCheck() {
    return this.problemMap.clearAllCheck()
  }

  setCheck(v: _MakeProblem['id']) {
    this.problemMap.setCheck(v)
  }

  isChecked(v: _MakeProblem['id']) {
    return this.problemMap.isChecked(v)
  }

  get isPartChecked() {
    return this.problemMap.isPartChecked
  }

  toggleCheck(v: _MakeProblem['id']) {
    this.problemMap.toggleCheck(v)
  }

  addProblem(problem: _MakeProblem) {
    this.problemMap.set(problem.id, problem)
  }

  adds(workbookColl: MakeProblemColl) {
    this.problemMap.adds(workbookColl.problemMap.entries())
  }

  updateProblem(id: _MakeProblem['id'], updateData: _MakeProblem) {
    const selected = this.problemMap?.get(id)
    if (selected) {
      selected.update(updateData)
    }
  }
}
